'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { Chin } from '#pplx/frontend/composer-sdk/components/Chin.js';
import { ComposerContainer } from '#pplx/frontend/composer-sdk/ComposerContainer.js';
import { ComposerEditorContext } from '#pplx/frontend/composer-sdk/ComposerEditorContext.js';
import { ComposerInput } from '#pplx/frontend/composer-sdk/ComposerInput.js';
import { ComposerProvider } from '#pplx/frontend/composer-sdk/ComposerProvider.js';
import { COMPOSER_NARROW_WIDTH_PX, ComposerWidthContext, } from '#pplx/frontend/composer-sdk/ComposerWidthContext.js';
import { useComposerContainerSize } from '#pplx/frontend/composer-sdk/hooks/useComposerContainerSize.js';
import { useComposerInputPlaceholder } from '#pplx/frontend/composer-sdk/hooks/useComposerInputPlaceholder.js';
import { useComposerRootEventListeners } from '#pplx/frontend/composer-sdk/hooks/useComposerRootEventListeners.js';
import { useLexicalInitialConfig } from '#pplx/frontend/composer-sdk/hooks/useLexicalInitialConfig.js';
import { usePreHydrationClickFallback } from '#pplx/frontend/composer-sdk/hooks/usePreHydrationClickFallback.js';
import { ControlledValuePlugin } from '#pplx/frontend/composer-sdk/internal/ControlledValuePlugin.js';
import { DraftTrackerPlugin } from '#pplx/frontend/composer-sdk/internal/DraftTrackerPlugin.js';
import { FileDropCapabilityHost } from '#pplx/frontend/composer-sdk/internal/file-drop/FileDropCapabilityHost.js';
import { useFileDropOverlay } from '#pplx/frontend/composer-sdk/internal/file-drop/FileDropCapabilityHostSupport.js';
import { FilePasteCapabilityHost } from '#pplx/frontend/composer-sdk/internal/file-paste/FilePasteCapabilityHost.js';
import { makeRegisterPasteHandler } from '#pplx/frontend/composer-sdk/internal/file-paste/registerPasteHandler.js';
import { FilePickerCapabilityHost } from '#pplx/frontend/composer-sdk/internal/file-picker/FilePickerCapabilityHost.js';
import { $setFocusPosition } from '#pplx/frontend/composer-sdk/internal/focusPosition.js';
import { FocusTrackerPlugin } from '#pplx/frontend/composer-sdk/internal/FocusTrackerPlugin.js';
import { InitialValueSeedPlugin } from '#pplx/frontend/composer-sdk/internal/InitialValueSeedPlugin.js';
import { defaultComposerContainerState } from '#pplx/frontend/composer-sdk/internal/input-state/reducers.js';
import { TextPlugin } from '#pplx/frontend/composer-sdk/internal/input/TextPlugin.js';
import { KeyboardArbitrationHost } from '#pplx/frontend/composer-sdk/internal/keyboard/KeyboardArbitrationHost.js';
import { KeyboardCapabilityHost } from '#pplx/frontend/composer-sdk/internal/keyboard/KeyboardCapabilityHost.js';
import { LinkCapabilityHost } from '#pplx/frontend/composer-sdk/internal/link/LinkCapabilityHost.js';
import { insertMention as insertMentionIntoEditor } from '#pplx/frontend/composer-sdk/internal/mention/insertMention.js';
import { removeMention as removeMentionFromEditor } from '#pplx/frontend/composer-sdk/internal/mention/removeMention.js';
import { resolveMentionIconRenderer } from '#pplx/frontend/composer-sdk/internal/mention/resolveMentionIconRenderer.js';
import { OnValueChangePlugin } from '#pplx/frontend/composer-sdk/internal/OnValueChangePlugin.js';
import { PlusMenuCapabilityHost } from '#pplx/frontend/composer-sdk/internal/plus-menu/PlusMenuCapabilityHost.js';
import { PrimaryActionCapabilityHost } from '#pplx/frontend/composer-sdk/internal/primary-action/PrimaryActionCapabilityHost.js';
import { SlotHost } from '#pplx/frontend/composer-sdk/internal/slots/SlotHost.js';
import { shouldAnimateChin, useSlotPlacements, } from '#pplx/frontend/composer-sdk/internal/slots/useSlotPlacements.js';
import { SubmitPlugin } from '#pplx/frontend/composer-sdk/internal/SubmitPlugin.js';
import { getComposerSurfaceClassNames } from '#pplx/frontend/composer-sdk/internal/surfaceStyles.js';
import { TypeToFocusPlugin } from '#pplx/frontend/composer-sdk/internal/type-to-focus/TypeToFocusPlugin.js';
import { TypeaheadPlugin } from '#pplx/frontend/composer-sdk/internal/typeahead/TypeaheadPlugin.js';
import { useComposerTypeahead } from '#pplx/frontend/composer-sdk/internal/typeahead/useComposerTypeahead.js';
import { useUserTakeover } from '#pplx/frontend/composer-sdk/internal/user-takeover/useUserTakeover.js';
import { useSubmitLock } from '#pplx/frontend/composer-sdk/internal/useSubmitLock.js';
import { ComposerContentEditable } from '#pplx/frontend/composer-sdk/lexical/ComposerContentEditable.js';
import { MentionIconRendererContext } from '#pplx/frontend/composer-sdk/lexical/MentionIconRendererContext.js';
import { resolveNodesForPlugins } from '#pplx/frontend/composer-sdk/lexical/nodes/resolveNodesForPlugins.js';
import { $appendTextToRoot, $replaceTextSuffixOrAppend, } from '#pplx/frontend/composer-sdk/lexical/utils/editorTextOperations.js';
import { getInputFocusState } from '#pplx/frontend/composer-sdk/lexical/utils/inputFocusState.js';
import { composerStyles, isFramedAppearance } from '#pplx/frontend/composer-sdk/styles.js';
import { useComposer } from '#pplx/frontend/composer-sdk/useComposer.js';
import { withoutCommandMenuMarker } from '#pplx/frontend/composer-sdk/withoutCommandMenuMarker.js';
import { ErrorBoundary } from '#pplx/frontend/platform-components/ErrorBoundary.js';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import classNames from 'classnames';
import { $createParagraphNode, $getRoot } from 'lexical';
import React, { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, } from 'react';
function getComposerDraftState(props) {
    if (props.value !== undefined) {
        return {
            draftMode: 'controlled',
            value: props.value,
            onValueChange: props.onValueChange,
        };
    }
    if (props.initialValue !== undefined) {
        return {
            draftMode: 'uncontrolled-seeded',
            initialValue: props.initialValue,
        };
    }
    return { draftMode: 'uncontrolled' };
}
export const Composer = React.memo(function Composer(props) {
    const status = props.status ?? 'idle';
    const plugins = props.plugins ?? EMPTY_ARRAY;
    const disabled = props.disabled ?? false;
    const draftState = getComposerDraftState(props);
    return (_jsx(ComposerProvider, { draftState: draftState, initialState: {
            ...defaultComposerContainerState,
            status,
            isDisabled: disabled,
            ...draftState,
        }, children: _jsx(FilePickerCapabilityHost, { plugins: plugins, disabled: disabled, children: _jsx(FileDropCapabilityHost, { plugins: plugins, disabled: disabled, children: _jsx(ComposerContent, { ...props, status: status }) }) }) }));
});
const ComposerContent = React.memo(function ComposerContent({ minRows, maxRows, enterKeyBehavior = 'submit', onClick, onEditableTouchStart, onFocus, onBlur, plugins = EMPTY_ARRAY, decorativeGlow, hasShadow, leadingAccessory, placeholder, ariaLabel, autoFocus = false, externalValueCaret = 'preserve', typeToFocus = true, typeahead: typeaheadConfig, ref, compact = false, compactShiftEnterBehavior = 'ignore', showPrimaryAction = false, onSubmit, submitLabel, submitDisabledTooltip, isSubmitDisabled = false, disabled = false, status, appearance, suggestionAttachment = 'input', mask, dashed, plusMenuPlacement, }) {
    useSyncComposerStatus(status);
    useSyncComposerDisabled(disabled);
    const { hasDraft, isActive } = useComposer();
    const { setContainer, observeContainerSize } = useComposerContainerSize();
    const [isNarrow, setIsNarrow] = useState(false);
    // WHY useLayoutEffect: subscribing measures the root synchronously, so the first paint
    // uses the correct presentation while sharing the existing container observer.
    useLayoutEffect(() => observeContainerSize(({ widthPx }) => {
        // A hidden pane keeps its last laid-out state.
        if (widthPx <= 0)
            return;
        setIsNarrow(widthPx < COMPOSER_NARROW_WIDTH_PX);
    }), [observeContainerSize]);
    const derivedNodes = useMemo(() => resolveNodesForPlugins(plugins), [plugins]);
    const initialConfig = useLexicalInitialConfig({ nodes: derivedNodes, editable: !disabled });
    const mentionIconRenderer = useMemo(() => resolveMentionIconRenderer(plugins), [plugins]);
    const [editor, setEditor] = useState(null);
    const disabledRef = useRef(disabled);
    disabledRef.current = disabled;
    useEffect(() => {
        editor?.setEditable(!disabled);
    }, [disabled, editor]);
    const registerPasteHandler = useMemo(() => (editor === null ? null : makeRegisterPasteHandler(editor)), [editor]);
    const [requestedDecorativeGlow, setRequestedDecorativeGlow] = useState();
    const inputRef = useRef(null);
    const inputFocusRef = useRef(null);
    const handleEarlyClick = usePreHydrationClickFallback({ editor, onClick });
    useComposerRootEventListeners({
        editor,
        enterKeyBehavior,
        onBlur: () => onBlur?.(),
        onClick,
        onEditableTouchStart,
        onFocus: () => onFocus?.(),
    });
    const { emitUserTakeover, subscribeUserTakeover } = useUserTakeover();
    const handleUserEdit = useCallback(() => emitUserTakeover('edit'), [emitUserTakeover]);
    const focus = useCallback((options) => {
        if (options !== undefined) {
            switch (options.position) {
                case 'start':
                case 'end':
                    break;
                case 'index':
                    // Validate before editor.update(), whose error handler otherwise logs the error.
                    if (!Number.isInteger(options.index) || options.index < 0) {
                        throw new TypeError(`Invalid cursor index ${options.index}: expected a nonnegative integer`);
                    }
                    break;
                default:
                    assertUnreachableValue(options);
            }
        }
        if (!editor) {
            return;
        }
        // editor.focus() moves DOM focus through selection reconciliation, which
        // non-browser DOMs may not translate into element focus. Focus the root
        // element directly so the gesture is deterministic, then let Lexical sync
        // its selection state.
        editor.getRootElement()?.focus({ preventScroll: true });
        editor.focus();
        if (options !== undefined) {
            editor.update(() => $setFocusPosition(options));
        }
    }, [editor]);
    const blur = useCallback(() => {
        editor?.blur();
    }, [editor]);
    const reset = useCallback(() => {
        // Pending plugin input must be discarded even while the editor is unavailable.
        emitUserTakeover('reset');
        editor?.update(() => {
            const root = $getRoot();
            root.clear();
            root.append($createParagraphNode());
        });
    }, [editor, emitUserTakeover]);
    const insertMention = useCallback((mention) => {
        if (editor === null || !plugins.some((plugin) => (plugin.mentions?.length ?? 0) > 0)) {
            return 'unavailable';
        }
        return insertMentionIntoEditor(editor, mention);
    }, [editor, plugins]);
    const removeMention = useCallback((identity) => {
        if (editor === null || !plugins.some((plugin) => (plugin.mentions?.length ?? 0) > 0)) {
            return 'unavailable';
        }
        return removeMentionFromEditor(editor, identity);
    }, [editor, plugins]);
    const append = useCallback((text, options) => {
        if (!text || editor === null || disabledRef.current)
            return false;
        editor.update(() => {
            $appendTextToRoot(text, options);
        }, { discrete: true });
        return true;
    }, [editor]);
    const replaceSuffixOrAppend = useCallback((operation) => {
        if (editor === null || disabledRef.current)
            return false;
        editor.update(() => {
            $replaceTextSuffixOrAppend(operation);
        }, { discrete: true });
        return true;
    }, [editor]);
    const replaceValue = useCallback((value) => {
        if (editor === null || disabledRef.current)
            return;
        editor.setEditorState(editor.parseEditorState(value, () => $getRoot().selectEnd()));
        focus();
    }, [editor, focus]);
    const controls = useMemo(() => ({
        append,
        focus,
        isReady: editor != null,
        replaceSuffixOrAppend,
        subscribeUserTakeover,
    }), [append, editor, focus, replaceSuffixOrAppend, subscribeUserTakeover]);
    const getFocusState = useCallback(() => inputFocusRef.current?.getFocusState() ?? getInputFocusState(null), []);
    useImperativeHandle(ref, () => ({
        blur,
        focus,
        getFocusState,
        insertMention,
        observeContainerSize,
        removeMention,
        replaceValue,
        reset,
    }), [
        blur,
        focus,
        getFocusState,
        insertMention,
        observeContainerSize,
        removeMention,
        replaceValue,
        reset,
    ]);
    const acquireSubmitLock = useSubmitLock();
    // Shared submit dispatch: snapshots the composer-owned draft and hands it to
    // the surface; the surface resets/refocuses through the imperative handle.
    const handleSubmitRequest = useCallback((keyEvent) => {
        // The editor arrives from EditorRefPlugin only after LexicalComposer
        // mounts, so a submit gesture racing initial mount finds it null.
        if (!editor || !acquireSubmitLock()) {
            return;
        }
        // Subscribers see the gesture before the consumer decides whether to accept it.
        emitUserTakeover('submit');
        const editorState = editor.getEditorState();
        onSubmit({
            // An open command menu's marker is transient UI state; strip it from
            // the snapshot — button submits and Enter with no highlighted row
            // bypass the menu's close paths, so it can still be in the tree here.
            state: withoutCommandMenuMarker(editorState.toJSON()),
            plainText: editorState.read(() => $getRoot().getTextContent()),
            // Spread conditionally so the field is genuinely absent (not
            // `undefined`) for button-fired submits, as documented.
            ...(keyEvent === undefined ? {} : { keyEvent }),
        });
    }, [acquireSubmitLock, editor, emitUserTakeover, onSubmit]);
    // The built-in button is a pointer gesture: the submission carries no keyEvent.
    const handleSubmitButtonClick = useCallback(() => handleSubmitRequest(), [handleSubmitRequest]);
    // Pointer submit requires a draft in both layouts; compact Enter may submit an empty query.
    const isSubmitBlocked = isSubmitDisabled || disabled || !hasDraft;
    const slotPlacements = useSlotPlacements(plugins);
    // All typeahead wiring (providers, overlay anchor, active-variant state). The
    // engine stays inert when no provider is registered.
    const composerTypeahead = useComposerTypeahead({
        plugins,
        overlayPlacement: typeaheadConfig?.overlayPlacement,
        isFramed: isFramedAppearance(appearance),
        suggestionAttachment,
    });
    const { typeaheadOverlay } = composerTypeahead;
    const { placeholderElement } = useComposerInputPlaceholder({
        placeholder,
        isFramed: isFramedAppearance(appearance),
        compact,
        // Expanded placeholders fit the empty editor's rows; compact placeholders truncate on one row.
        maxLines: compact ? undefined : (minRows ?? 1),
        className: classNames(composerStyles.placeholder.base.join(' '), compact && composerStyles.placeholder.compact),
    });
    const contentEditable = useMemo(() => {
        return compact ? (_jsx(ComposerContentEditable, { ref: inputFocusRef, compact: true, "aria-label": ariaLabel, "aria-placeholder": placeholder ?? '', placeholder: placeholder ? placeholderElement : undefined })) : (_jsx(ComposerContentEditable, { ref: inputFocusRef, minRows: minRows, maxRows: maxRows, "aria-label": ariaLabel, "aria-placeholder": placeholder ?? '', placeholder: placeholder ? placeholderElement : undefined }));
    }, [ariaLabel, compact, maxRows, minRows, placeholder, placeholderElement]);
    // Framed eyebrows live in the recessed surround; other eyebrows still tuck behind the box.
    // Standalone `banner` occupants stay in flow above both layouts.
    const hasEyebrow = (slotPlacements.eyebrow?.length ?? 0) > 0;
    const isExpanded = !compact;
    const inputHeader = useMemo(() => _jsx(SlotHost, { placements: slotPlacements.inputHeader }), [slotPlacements.inputHeader]);
    const fileDropOverlay = useFileDropOverlay();
    const hasPluginContent = plugins.some((plugin) => plugin.hasContent === true);
    const chinPlacements = slotPlacements.chin;
    const hasVisibleChin = chinPlacements?.some(({ visible }) => visible !== false) ?? false;
    const chinVisible = chinPlacements?.some(({ visible }) => visible !== undefined)
        ? hasVisibleChin
        : undefined;
    const chinAndCommandMenu = (_jsxs(_Fragment, { children: [(slotPlacements.chin?.length ?? 0) > 0 && (_jsx(Chin, { transparent: isFramedAppearance(appearance), isVisible: chinVisible, animate: shouldAnimateChin(slotPlacements), children: _jsx(SlotHost, { placements: slotPlacements.chin }) })), composerTypeahead.framedCommandMenuAnchor] }));
    const content = (_jsx(MentionIconRendererContext.Provider, { value: mentionIconRenderer, children: _jsx(ComposerEditorContext.Provider, { value: controls, children: _jsxs("div", { ref: setContainer, children: [_jsx("div", { className: "empty:hidden flex flex-col gap-4 pb-4", children: _jsx(SlotHost, { placements: slotPlacements.banner }) }), !isFramedAppearance(appearance) && _jsx(SlotHost, { placements: slotPlacements.eyebrow }), _jsx("div", { className: classNames('relative', hasEyebrow && !isFramedAppearance(appearance) && 'z-[2]'), children: _jsxs("div", { className: classNames('relative', 
                            // Sibling overlays must share the container's attachment surface.
                            getComposerSurfaceClassNames({ appearance, mask, suggestionAttachment })), children: [_jsxs(ComposerContainer, { isActive: isActive, isExpanded: isExpanded, isMobileUserAgent: false, inputRef: inputRef, appearance: appearance, suggestionAttachment: suggestionAttachment, mask: mask, dashed: dashed, attachedHeader: isFramedAppearance(appearance) && hasEyebrow ? (_jsx(SlotHost, { placements: slotPlacements.eyebrow })) : undefined, 
                                    // A spotlight surface (e.g. the command palette) elevates the box so it and its
                                    // attached dropdown read as one raised card, floating over undimmed page content.
                                    hasShadow: hasShadow || composerTypeahead.isSpotlightSurface, shadowElevation: composerTypeahead.isSpotlightSurface ? 'xl' : undefined, decorativeGlow: decorativeGlow ?? requestedDecorativeGlow, onDecorativeGlowChange: setRequestedDecorativeGlow, attachedDropdownPlacement: composerTypeahead.attachedDropdownPlacement, attachedDropdownKind: composerTypeahead.attachedDropdownKind, framedCommandMenuOpen: composerTypeahead.isFramedCommandMenuOpen, framedChinVisible: hasVisibleChin, 
                                    // The `inputHeader` slot renders inside the box, above the editor (e.g. the quote pill).
                                    // Unlike `banner` and `eyebrow` (which sit outside/above the box), it lives in the
                                    // box's top region.
                                    topContent: inputHeader, attachmentsList: _jsx(SlotHost, { placements: slotPlacements.attachmentTray }), bottomContent: (slotPlacements.chin?.length ?? 0) > 0 ||
                                        composerTypeahead.framedCommandMenuAnchor ? (isFramedAppearance(appearance) ? (_jsx("div", { className: "contents", children: chinAndCommandMenu })) : (chinAndCommandMenu)) : undefined, children: [_jsx(ComposerInput, { ref: inputRef, isExpanded: isExpanded, leadingAccessory: leadingAccessory, onClick: handleEarlyClick, children: _jsx(LexicalComposer, { initialConfig: initialConfig, children: _jsxs(KeyboardArbitrationHost, { children: [_jsx(KeyboardCapabilityHost, { plugins: plugins, onUserEdit: handleUserEdit }), _jsx(TextPlugin, { contentEditable: contentEditable, ErrorBoundary: ErrorBoundary }), _jsx(EditorRefPlugin, { editorRef: setEditor }), _jsx(FilePasteCapabilityHost, { plugins: plugins, disabled: disabled, registerPasteHandler: registerPasteHandler }), _jsx(LinkCapabilityHost, { plugins: plugins }), _jsx(TypeToFocusPlugin, { enabled: typeToFocus && !disabled }), _jsx(HistoryPlugin, {}), _jsx(ControlledValuePlugin, { autoFocus: autoFocus, externalValueCaret: externalValueCaret }), _jsx(OnValueChangePlugin, {}), _jsx(DraftTrackerPlugin, { hasPluginContent: hasPluginContent }), _jsx(FocusTrackerPlugin, {}), _jsx(InitialValueSeedPlugin, {}), autoFocus && _jsx(AutoFocusPlugin, {}), composerTypeahead.providers.length > 0 && (_jsx(TypeaheadPlugin, { typeaheadRef: typeaheadConfig?.ref, providers: composerTypeahead.providers, disabled: disabled, menuAlwaysOpen: typeaheadConfig?.menuAlwaysOpen, wrapNavigation: typeaheadConfig?.wrapNavigation, overlayPlacement: typeaheadConfig?.overlayPlacement, commandMenu: typeaheadConfig?.commandMenu, overlayAnchorRef: composerTypeahead.overlayAnchorRef, commandMenuOverlay: composerTypeahead.commandMenuOverlay, onActiveVariantChange: composerTypeahead.onActiveVariantChange })), _jsx(SubmitPlugin, { compact: compact, compactShiftEnterBehavior: compactShiftEnterBehavior, enterKeyBehavior: enterKeyBehavior, 
                                                            // Compact Enter stays available on an empty draft (surfaces such
                                                            // as a command palette submit empty queries); the expanded
                                                            // shortcut is draft-gated like the built-in button.
                                                            isDisabled: compact ? isSubmitDisabled || disabled : isSubmitBlocked, onSubmit: handleSubmitRequest })] }) }) }), _jsxs("div", { className: classNames('empty:hidden', ...composerStyles.attribution.left.base, isExpanded
                                                ? composerStyles.attribution.left.expanded
                                                : composerStyles.attribution.left.compact), children: [_jsx(PlusMenuCapabilityHost, { plugins: plugins, placement: plusMenuPlacement, disabled: disabled }), _jsx(SlotHost, { placements: slotPlacements.leftAttribution })] }), _jsxs("div", { className: classNames('empty:hidden', ...composerStyles.attribution.right.base, isExpanded
                                                ? composerStyles.attribution.right.expanded
                                                : composerStyles.attribution.right.compact), children: [_jsx(SlotHost, { placements: slotPlacements.rightAttribution }), (isExpanded || showPrimaryAction) && (_jsx(PrimaryActionCapabilityHost, { plugins: plugins, submitLabel: submitLabel, submitDisabledTooltip: isSubmitDisabled ? submitDisabledTooltip : undefined, isSubmitDisabled: isSubmitBlocked, onSubmit: handleSubmitButtonClick }))] }), composerTypeahead.inputCommandMenuAnchor] }), typeaheadOverlay, fileDropOverlay] }) })] }) }) }));
    // Slot content reads the composer's own width class rather than the viewport.
    return _jsx(ComposerWidthContext.Provider, { value: isNarrow, children: content });
});
function useSyncComposerStatus(status) {
    const { setStatus } = useComposer();
    useEffect(() => {
        setStatus(status);
    }, [setStatus, status]);
}
function useSyncComposerDisabled(isDisabled) {
    const { setDisabled } = useComposer();
    useEffect(() => {
        setDisabled(isDisabled);
    }, [isDisabled, setDisabled]);
}
//# sourceMappingURL=Composer.js.map